/**
 * HomepageRenderer.js
 * Shared homepage rendering components used by both the public Home.js page
 * and the Admin Homepage Preview. Only the data source differs.
 */
import { useState, useEffect, useRef, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { Play, Eye, Tv, ChevronLeft, ChevronRight, Zap, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

// ============ CONSTANTS ============

export const DEFAULT_HERO =
  "https://static.prod-images.emergentagent.com/jobs/da293390-17b3-4a24-b107-f9f013e80fde/images/4db205155861085ef7e0eb5842a9f67cf7ec2f982a9bfd58790512d194599fcd.png";

// ============ PREVIEW CONTEXT ============

export const PreviewContext = createContext({ previewMode: false, filterPublishable: false });

// ============ HELPERS ============

export function imgUrl(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return path;
}

export function isExternalUrl(url) {
  if (!url) return false;
  return url.startsWith("http://") || url.startsWith("https://");
}

export function localizedText(locale, fr, en, fallback) {
  const text = locale === "fr" ? (fr || en) : (en || fr);
  return text || fallback || "";
}

// ============ PREVIEW-SAFE LINK ============
// In preview mode, links are rendered as <a> with navigation disabled.

function SafeLink({ to, children, className, style, ...props }) {
  const { previewMode } = useContext(PreviewContext);
  if (previewMode) {
    return (
      <a href="#" className={className} style={style}
        onClick={(e) => e.preventDefault()} {...props}>
        {children}
      </a>
    );
  }
  return <Link to={to || "/"} className={className} style={style} {...props}>{children}</Link>;
}

export function SmartLink({ to, children, className, style, ...props }) {
  const { previewMode } = useContext(PreviewContext);
  if (!to || to === "#") return <span className={className} style={style} {...props}>{children}</span>;
  if (previewMode) {
    return (
      <span className={className} style={{ ...style, cursor: "default" }} {...props}>
        {children}
      </span>
    );
  }
  if (isExternalUrl(to)) {
    return <a href={to} target="_blank" rel="noopener noreferrer" className={className} style={style} {...props}>{children}</a>;
  }
  return <Link to={to} className={className} style={style} {...props}>{children}</Link>;
}

// ============ PREVIEW STATUS BADGE ============

const STATUS_BADGE_STYLES = {
  published: "bg-green-500/80 text-white",
  draft: "bg-amber-500/80 text-white",
  hidden: "bg-slate-500/80 text-white",
  inactive: "bg-red-500/80 text-white",
  scheduled: "bg-blue-500/80 text-white",
  expired: "bg-rose-600/80 text-white",
  archived: "bg-gray-600/80 text-white",
};

function PreviewBadge({ status }) {
  if (!status || status === "published") return null;
  return (
    <span className={cn(
      "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm",
      STATUS_BADGE_STYLES[status] || STATUS_BADGE_STYLES.draft
    )} data-testid={`preview-badge-${status}`}>
      {status}
    </span>
  );
}

function PreviewSectionWrapper({ section, children }) {
  const { previewMode } = useContext(PreviewContext);
  if (!previewMode) return children;

  const status = section?._preview_status || section?.status || "draft";

  return (
    <div className="relative group/preview" data-testid={`preview-section-${section?.id || section?.type}`}>
      {status !== "published" && (
        <div className="absolute top-2 right-2 z-30 flex items-center gap-1.5">
          <PreviewBadge status={status} />
        </div>
      )}
      {status !== "published" && (
        <div className="absolute inset-0 z-20 rounded-[24px] border-2 border-dashed pointer-events-none"
          style={{
            borderColor: status === "draft" ? "rgba(245,158,11,0.4)" :
              status === "hidden" ? "rgba(100,116,139,0.4)" :
              status === "inactive" ? "rgba(239,68,68,0.4)" :
              status === "scheduled" ? "rgba(59,130,246,0.4)" :
              status === "expired" ? "rgba(225,29,72,0.4)" : "rgba(100,116,139,0.3)",
          }}
        />
      )}
      <div className={cn(status !== "published" && "opacity-75")}>{children}</div>
    </div>
  );
}

// ============ REUSABLE COMPONENTS ============

export function ResponsiveImage({ desktop, tablet, mobile, fallback, alt, className, loading: loadProp = "lazy", role }) {
  const src = desktop || tablet || mobile || fallback;
  if (!src) return null;
  const dUrl = imgUrl(desktop || src);
  const tUrl = imgUrl(tablet || desktop || src);
  const mUrl = imgUrl(mobile || desktop || src);
  return (
    <picture>
      {mUrl !== dUrl && <source media="(max-width: 640px)" srcSet={mUrl} />}
      {tUrl !== dUrl && <source media="(max-width: 1024px)" srcSet={tUrl} />}
      <img src={dUrl} alt={alt || ""} className={className} loading={loadProp}
        referrerPolicy="no-referrer" role={role}
        onError={(e) => { if (fallback && e.target.src !== fallback) e.target.src = fallback; }} />
    </picture>
  );
}

export function ChannelCard({ channel, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.03, duration: 0.4 }}
      whileHover={{ y: -6, transition: { duration: 0.2 } }}
    >
      <SafeLink to={`/watch/${channel.id}`} data-testid={`channel-card-${channel.id}`}
        className="group block rounded-[18px] overflow-hidden border border-white/[0.06] bg-[#0F172A] hover:border-[#FFC928]/30 hover:shadow-[0_8px_30px_rgba(255,201,40,0.1)] transition-all duration-300">
        <div className="aspect-video relative bg-[#050814] overflow-hidden">
          <img src={channel.logo_url || DEFAULT_HERO} alt={channel.name || "Channel"}
            className="w-full h-full object-contain p-5 group-hover:scale-110 transition-transform duration-500" referrerPolicy="no-referrer" loading="lazy"
            onError={(e) => { e.target.src = DEFAULT_HERO; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent opacity-60" />
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1 px-2 py-0.5 rounded-lg bg-red-500/90 backdrop-blur-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-white live-pulse" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wider">Live</span>
          </div>
          <div className="absolute top-2.5 right-2.5 px-1.5 py-0.5 rounded bg-white/10 backdrop-blur-sm text-[10px] font-bold text-white/80">HD</div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 bg-[#FFC928] rounded-full flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300 shadow-[0_0_25px_rgba(255,201,40,0.4)]">
              <Play size={22} className="text-black fill-black ml-0.5" />
            </div>
          </div>
        </div>
        <div className="p-3.5">
          <h4 className="font-semibold text-sm truncate text-white mb-1 group-hover:text-[#FFC928] transition-colors">{channel.name}</h4>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium">{channel.category_name || ""}</span>
            <div className="flex items-center gap-1 text-[10px] text-[#94A3B8]"><Eye size={10} /><span>{channel.views || 0}</span></div>
          </div>
        </div>
      </SafeLink>
    </motion.div>
  );
}

export function HorizontalScroll({ children, title, icon: Icon, iconColor }) {
  const ref = useRef(null);
  const scroll = (dir) => { if (ref.current) ref.current.scrollBy({ left: dir * 340, behavior: "smooth" }); };
  if (!children || (Array.isArray(children) && children.length === 0)) return null;
  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-5 px-1">
        <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2.5 text-white" style={{ fontFamily: "Poppins" }}>
          {Icon && <Icon size={22} className={iconColor} />}{title}
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={() => scroll(-1)} aria-label="Scroll left" className="w-8 h-8 rounded-full bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-[#94A3B8] hover:text-white hover:bg-white/10 transition-colors"><ChevronLeft size={16} /></button>
          <button onClick={() => scroll(1)} aria-label="Scroll right" className="w-8 h-8 rounded-full bg-white/[0.05] border border-white/[0.08] flex items-center justify-center text-[#94A3B8] hover:text-white hover:bg-white/10 transition-colors"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-1 px-1">{children}</div>
    </section>
  );
}

// ============ SECTION RENDERERS ============

export function HeroSliderSection({ sliders, locale, channels, settings, isPremium, t }) {
  const { previewMode } = useContext(PreviewContext);
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);
  const slides = sliders.length > 0 ? sliders : null;

  useEffect(() => {
    if (!slides || slides.length <= 1) return;
    timerRef.current = setInterval(() => setCurrent((p) => (p + 1) % slides.length), 6000);
    return () => clearInterval(timerRef.current);
  }, [slides]);

  const goTo = (i) => {
    setCurrent(i);
    clearInterval(timerRef.current);
    if (slides && slides.length > 1)
      timerRef.current = setInterval(() => setCurrent((p) => (p + 1) % slides.length), 6000);
  };

  if (!slides) {
    const firstChannel = channels[0];
    return (
      <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
        className="relative rounded-[24px] overflow-hidden mb-10 min-h-[280px] sm:min-h-[360px]" data-testid="hero-section">
        <img src={DEFAULT_HERO} alt="" role="presentation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050814] via-[#050814]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050814] via-transparent to-transparent" />
        <div className="relative z-10 flex flex-col justify-end h-full min-h-[280px] sm:min-h-[360px] p-6 sm:p-10 lg:p-14">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3 leading-tight max-w-2xl" style={{ fontFamily: "Poppins" }}>
              {t("hero.title1")}<br /><span className="text-[#FFC928]">{t("hero.title2")}</span>
            </h1>
            <p className="text-[#94A3B8] text-sm sm:text-base max-w-lg mb-6">{t("hero.subtitle")}</p>
            <div className="flex flex-wrap gap-3">
              <SafeLink to={firstChannel ? `/watch/${firstChannel.id}` : "/categories"} className="px-6 py-3 bg-[#FFC928] text-black rounded-xl font-bold text-sm hover:shadow-[0_0_25px_rgba(255,201,40,0.4)] transition-all flex items-center gap-2" data-testid="hero-watch-btn">
                <Play size={16} className="fill-black" /> {t("hero.watchLive")}
              </SafeLink>
              <SafeLink to="/categories" className="px-6 py-3 bg-white/[0.08] border border-white/[0.12] text-white rounded-xl font-bold text-sm hover:bg-white/[0.12] transition-all backdrop-blur-sm flex items-center gap-2" data-testid="hero-channels-btn">
                <Tv size={16} /> {t("hero.viewChannels")}
              </SafeLink>
            </div>
          </motion.div>
        </div>
      </motion.section>
    );
  }

  const slide = slides[current] || slides[0];
  if (!slide) return null;
  const title = localizedText(locale, slide.title_fr, slide.title_en);
  const subtitle = localizedText(locale, slide.subtitle_fr, slide.subtitle_en);
  const desc = localizedText(locale, slide.description_fr, slide.description_en);
  const altText = localizedText(locale, slide.alt_text_fr, slide.alt_text_en, title || "Hero");
  const cta1Text = localizedText(locale, slide.btn_text_fr, slide.btn_text_en);
  const cta2Text = localizedText(locale, slide.btn2_text_fr, slide.btn2_text_en);
  const textPos = slide.text_position || "left";
  const overlayOpacity = slide.overlay_opacity ?? 0.5;

  return (
    <section className="relative rounded-[24px] overflow-hidden mb-10 min-h-[280px] sm:min-h-[360px]" data-testid="hero-section">
      <AnimatePresence mode="wait">
        <motion.div key={current} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0">
          <ResponsiveImage
            desktop={slide.desktop_image} tablet={slide.tablet_image} mobile={slide.mobile_image}
            fallback={slide.image_url || DEFAULT_HERO} alt={altText}
            className="w-full h-full object-cover"
            loading={current === 0 ? "eager" : "lazy"} role="presentation" />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0" style={{ backgroundColor: `rgba(5,8,20,${overlayOpacity})` }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050814] via-[#050814]/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050814] via-transparent to-transparent" />

      <div className={cn(
        "relative z-10 flex flex-col justify-end h-full min-h-[280px] sm:min-h-[360px] p-6 sm:p-10 lg:p-14",
        textPos === "center" && "items-center text-center",
        textPos === "right" && "items-end text-right",
      )}>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="max-w-2xl">
          {title && <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3 leading-tight" style={{ fontFamily: "Poppins" }}>{title}</h1>}
          {subtitle && <p className="text-lg sm:text-xl text-[#FFC928] font-semibold mb-2">{subtitle}</p>}
          {desc && <p className="text-[#94A3B8] text-sm sm:text-base max-w-lg mb-6">{desc}</p>}
          <div className="flex flex-wrap gap-3">
            {cta1Text && slide.btn_url && (
              <SmartLink to={slide.btn_url} className="px-6 py-3 bg-[#FFC928] text-black rounded-xl font-bold text-sm hover:shadow-[0_0_25px_rgba(255,201,40,0.4)] transition-all flex items-center gap-2"
                style={slide.btn_color ? { backgroundColor: slide.btn_color } : {}} data-testid="hero-cta-1">
                <Play size={16} className="fill-black" /> {cta1Text}
              </SmartLink>
            )}
            {cta2Text && slide.btn2_url && (
              <SmartLink to={slide.btn2_url} className="px-6 py-3 bg-white/[0.08] border border-white/[0.12] text-white rounded-xl font-bold text-sm hover:bg-white/[0.12] transition-all backdrop-blur-sm" data-testid="hero-cta-2">
                {cta2Text}
              </SmartLink>
            )}
            {!cta1Text && !cta2Text && (
              <SafeLink to={channels[0] ? `/watch/${channels[0].id}` : "/categories"} className="px-6 py-3 bg-[#FFC928] text-black rounded-xl font-bold text-sm hover:shadow-[0_0_25px_rgba(255,201,40,0.4)] transition-all flex items-center gap-2">
                <Play size={16} className="fill-black" /> {t("hero.watchLive")}
              </SafeLink>
            )}
          </div>
        </motion.div>
      </div>

      {/* Slide indicators with preview badges */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2 items-end">
          {slides.map((s, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              {previewMode && s._preview_status && s._preview_status !== "published" && (
                <PreviewBadge status={s._preview_status} />
              )}
              <button onClick={() => goTo(i)} aria-label={`Go to slide ${i + 1}`}
                className={cn("h-1.5 rounded-full transition-all", i === current ? "w-8 bg-[#FFC928]" : "w-3 bg-white/30 hover:bg-white/50")}
                data-testid={`hero-dot-${i}`} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function FeaturedChannelCard({ channel }) {
  return (
    <SafeLink to={`/watch/${channel.id}`} data-testid={`featured-channel-${channel.id}`}
      className="group flex-shrink-0 w-[280px] sm:w-[320px] rounded-[20px] overflow-hidden bg-[#0F172A] border border-white/[0.06] hover:border-[#FFC928]/30 hover:shadow-[0_8px_30px_rgba(255,201,40,0.1)] transition-all duration-300">
      <div className="h-44 relative overflow-hidden">
        <img src={channel.logo_url || DEFAULT_HERO} alt={channel.name || "Channel"}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          referrerPolicy="no-referrer" loading="lazy"
          onError={(e) => { e.target.src = DEFAULT_HERO; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-0.5 rounded-lg bg-red-500/90 backdrop-blur-sm">
          <div className="w-1.5 h-1.5 rounded-full bg-white live-pulse" /><span className="text-[10px] font-bold text-white">LIVE</span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 bg-[#FFC928] rounded-full flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300 shadow-[0_0_25px_rgba(255,201,40,0.4)]">
            <Play size={24} className="text-black fill-black ml-0.5" />
          </div>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-white truncate group-hover:text-[#FFC928] transition-colors">{channel.name}</h3>
        <div className="flex items-center gap-2 mt-1 text-xs text-[#94A3B8]">
          <span>{channel.category_name || ""}</span>
          {channel.country_name && <><span className="text-white/20">·</span><span>{channel.country_name}</span></>}
        </div>
      </div>
    </SafeLink>
  );
}

export function SmallChannelCard({ channel, accentColor = "emerald" }) {
  const colorMap = {
    emerald: { hover: "hover:border-emerald-500/30", text: "group-hover:text-emerald-400" },
    orange: { hover: "hover:border-orange-500/30", text: "group-hover:text-orange-400" },
    blue: { hover: "hover:border-blue-500/30", text: "group-hover:text-blue-400" },
    purple: { hover: "hover:border-purple-500/30", text: "group-hover:text-purple-400" },
  };
  const c = colorMap[accentColor] || colorMap.emerald;
  return (
    <SafeLink to={`/watch/${channel.id}`}
      className={cn("group flex-shrink-0 w-[200px] sm:w-[220px] rounded-[18px] overflow-hidden bg-[#0F172A] border border-white/[0.06] transition-all duration-300", c.hover)}>
      <div className="aspect-video relative overflow-hidden bg-[#050814]">
        <img src={channel.logo_url || DEFAULT_HERO} alt={channel.name || "Channel"}
          className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500"
          referrerPolicy="no-referrer" loading="lazy"
          onError={(e) => { e.target.src = DEFAULT_HERO; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent opacity-60" />
      </div>
      <div className="p-3">
        <h4 className={cn("text-white text-sm font-semibold truncate transition-colors", c.text)}>{channel.name}</h4>
        <div className="flex items-center gap-1 text-[10px] text-[#94A3B8] mt-0.5"><Eye size={10} />{channel.views || 0}</div>
      </div>
    </SafeLink>
  );
}

export function BannerSection({ banners, placement, locale }) {
  const { previewMode } = useContext(PreviewContext);
  if (!Array.isArray(banners)) return null;
  const matchedBanners = banners.filter((b) => b.placement === placement);
  if (matchedBanners.length === 0) return null;
  const b = matchedBanners[0];
  const title = localizedText(locale, b.title_fr, b.title_en);
  const desc = localizedText(locale, b.description_fr, b.description_en);
  const btnText = localizedText(locale, b.btn_text_fr, b.btn_text_en);
  const altText = localizedText(locale, b.alt_text_fr, b.alt_text_en, title);
  const hasImage = b.desktop_image || b.image;

  return (
    <section className="mb-10 relative" data-testid={`banner-${placement}`}>
      {previewMode && b._preview_status && b._preview_status !== "published" && (
        <div className="absolute top-2 right-2 z-30">
          <PreviewBadge status={b._preview_status} />
        </div>
      )}
      <div className="relative overflow-hidden rounded-[24px]" style={{ backgroundColor: b.background_color || "#0F172A" }}>
        {hasImage && (
          <>
            <ResponsiveImage desktop={b.desktop_image || b.image} tablet={b.tablet_image} mobile={b.mobile_image}
              fallback={DEFAULT_HERO} alt={altText} className="absolute inset-0 w-full h-full object-cover" role="presentation" />
            <div className="absolute inset-0" style={{ backgroundColor: `rgba(0,0,0,${b.overlay_opacity ?? 0.3})` }} />
          </>
        )}
        <div className={cn("relative p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 min-h-[120px]",
          b.text_position === "center" && "text-center items-center",
          b.text_position === "right" && "text-right items-end")}>
          <div>
            {title && <h3 className="text-2xl sm:text-3xl font-extrabold mb-2" style={{ color: b.text_color || "#FFFFFF", fontFamily: "Poppins" }}>{title}</h3>}
            {desc && <p className="text-sm max-w-lg" style={{ color: b.text_color || "#FFFFFF", opacity: 0.8 }}>{desc}</p>}
          </div>
          {btnText && b.btn_url && (
            <SmartLink to={b.btn_url} className="shrink-0 px-8 py-3.5 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
              style={{ backgroundColor: b.btn_color || "#FFC928", color: "#000" }} data-testid={`banner-cta-${placement}`}>
              {btnText}
            </SmartLink>
          )}
        </div>
      </div>
    </section>
  );
}

export function PremiumBannerSection({ settings, isPremium, t }) {
  if (settings?.membership_enabled === false || isPremium) return null;
  return (
    <section className="mb-10" data-testid="premium-cta">
      <div className="relative overflow-hidden rounded-[24px] p-8 sm:p-10" style={{ background: "linear-gradient(135deg, #1a1206 0%, #050814 50%, #0a0e2a 100%)" }}>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, rgba(255,201,40,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59,130,246,0.2) 0%, transparent 50%)" }} />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-2" style={{ fontFamily: "Poppins" }}>
              {t("premium.unlockTitle")} <span className="text-[#FFC928]">{t("premium.unlockHighlight")}</span>
            </h3>
            <div className="flex flex-wrap gap-4 mt-3">
              {[t("premium.adFree"), t("premium.hdQuality"), t("premium.unlimitedStreaming"), t("premium.prioritySupport")].map((b) => (
                <div key={b} className="flex items-center gap-1.5 text-sm text-[#94A3B8]"><div className="w-1.5 h-1.5 rounded-full bg-[#FFC928]" />{b}</div>
              ))}
            </div>
          </div>
          <SafeLink to="/membership" data-testid="cta-subscribe-btn"
            className="shrink-0 px-8 py-3.5 bg-gradient-to-r from-[#FFC928] to-[#FFD85A] text-black rounded-xl font-bold text-sm hover:shadow-[0_0_30px_rgba(255,201,40,0.4)] transition-all">
            {t("premium.becomePremium")}
          </SafeLink>
        </div>
      </div>
    </section>
  );
}

export function CategoryPillsSection({ categories, locale, t, onSelect, selected }) {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  return (
    <section className="mb-8" data-testid="category-pills-section">
      <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
        <button onClick={() => onSelect(null)}
          className={cn("px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all border",
            !selected ? "bg-[#FFC928] text-black border-[#FFC928] shadow-[0_0_15px_rgba(255,201,40,0.2)]" : "bg-white/[0.05] text-[#94A3B8] border-white/[0.08] hover:text-white hover:bg-white/[0.08]")}
          data-testid="category-all">
          {t("home.allCategories")}
        </button>
        {categories.map((cat) => (
          <button key={cat.id} onClick={() => onSelect(selected === cat.id ? null : cat.id)}
            className={cn("px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all border",
              selected === cat.id ? "bg-[#FFC928] text-black border-[#FFC928] shadow-[0_0_15px_rgba(255,201,40,0.2)]" : "bg-white/[0.05] text-[#94A3B8] border-white/[0.08] hover:text-white hover:bg-white/[0.08]")}
            data-testid={`category-pill-${cat.id}`}>
            {localizedText(locale, cat.name_fr, cat.name_en, cat.name)}
          </button>
        ))}
      </div>
    </section>
  );
}

export function CategoryRowSection({ categories, channels, locale, sectionConfig }) {
  const catId = sectionConfig?.category_id;
  if (!catId) {
    const topCats = (categories || []).filter((c) => c.channel_count > 0).slice(0, 8);
    if (topCats.length === 0) return null;
    return (
      <section className="mb-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {topCats.map((cat) => (
            <SafeLink key={cat.id} to={`/categories/${cat.slug}`}
              className="relative rounded-xl overflow-hidden h-28 group border border-white/[0.06] hover:border-[#FFC928]/30 transition-all">
              {cat.image_url && <img src={cat.image_url} alt={localizedText(locale, cat.name_fr, cat.name_en, cat.name)} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20" />
              <div className="relative z-10 h-full flex flex-col justify-end p-3">
                <h4 className="text-white font-semibold text-sm">{localizedText(locale, cat.name_fr, cat.name_en, cat.name)}</h4>
                <span className="text-[10px] text-[#94A3B8]">{cat.channel_count || 0} channels</span>
              </div>
            </SafeLink>
          ))}
        </div>
      </section>
    );
  }
  const catChannels = (channels || []).filter((ch) => ch.category_id === catId).slice(0, 12);
  if (catChannels.length === 0) return null;
  const cat = (categories || []).find((c) => c.id === catId);
  const catName = cat ? localizedText(locale, cat.name_fr, cat.name_en, cat.name) : "";
  return (
    <HorizontalScroll title={catName} icon={Tv} iconColor="text-blue-400">
      {catChannels.map((ch) => <SmallChannelCard key={ch.id} channel={ch} accentColor="blue" />)}
    </HorizontalScroll>
  );
}

// ============ SECTION DISPATCHER ============

export function renderSection(section, data, locale, t) {
  if (!section || !section.type) return null;
  const type = section.type;
  const cfg = section.config || {};

  let content = null;

  switch (type) {
    case "hero_slider":
      content = <HeroSliderSection key={section.id || type} sliders={data.sliders} locale={locale} channels={data.allChannels} settings={data.settings} isPremium={data.isPremium} t={t} />;
      break;

    case "category_pills":
      content = <CategoryPillsSection key={section.id || type} categories={data.categories} locale={locale} t={t} onSelect={data.onSelectCategory} selected={data.selectedCategory} />;
      break;

    case "featured_channels":
      if (!data.featured_channels || data.featured_channels.length === 0) return null;
      content = (
        <HorizontalScroll key={section.id || type} title={localizedText(locale, section.title_fr, section.title_en, locale === "fr" ? "Chaînes en Vedette" : "Featured Channels")} icon={Sparkles} iconColor="text-[#FFC928]">
          {data.featured_channels.map((ch) => <FeaturedChannelCard key={ch.id} channel={ch} />)}
        </HorizontalScroll>
      );
      break;

    case "trending_channels":
      if (!data.trending_channels || data.trending_channels.length === 0) return null;
      content = (
        <HorizontalScroll key={section.id || type} title={localizedText(locale, section.title_fr, section.title_en, locale === "fr" ? "Tendance" : "Trending Now")} icon={Zap} iconColor="text-orange-400">
          {data.trending_channels.map((ch) => <SmallChannelCard key={ch.id} channel={ch} accentColor="orange" />)}
        </HorizontalScroll>
      );
      break;

    case "recently_added":
      if (!data.recently_added || data.recently_added.length === 0) return null;
      content = (
        <HorizontalScroll key={section.id || type} title={localizedText(locale, section.title_fr, section.title_en, locale === "fr" ? "Récemment ajoutées" : "Recently Added")} icon={Sparkles} iconColor="text-green-400">
          {data.recently_added.map((ch) => <SmallChannelCard key={ch.id} channel={ch} accentColor="emerald" />)}
        </HorizontalScroll>
      );
      break;

    case "most_watched":
      if (!data.trending_channels || data.trending_channels.length === 0) return null;
      content = (
        <HorizontalScroll key={section.id || type} title={localizedText(locale, section.title_fr, section.title_en, locale === "fr" ? "Les Plus Regardées" : "Most Watched")} icon={Eye} iconColor="text-purple-400">
          {data.trending_channels.slice(0, 12).map((ch) => <SmallChannelCard key={ch.id} channel={ch} accentColor="purple" />)}
        </HorizontalScroll>
      );
      break;

    case "premium_banner":
      content = <PremiumBannerSection key={section.id || type} settings={data.settings} isPremium={data.isPremium} t={t} />;
      break;

    case "promotional_banner":
    case "promo_banner":
      content = <BannerSection key={section.id || type} banners={data.banners} placement={cfg.placement || "homepage_middle"} locale={locale} />;
      break;

    case "category_row":
      content = <CategoryRowSection key={section.id || type} categories={data.categories} channels={data.allChannels} locale={locale} sectionConfig={cfg} />;
      break;

    case "country_row":
      return null;

    case "custom_text": {
      const text = localizedText(locale, cfg.text_fr, cfg.text_en, cfg.text);
      if (!text) return null;
      content = (
        <section key={section.id || type} className="mb-10">
          <div className="bg-[#0F172A] border border-white/[0.06] rounded-[24px] p-8">
            <div className="text-white prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: text }} />
          </div>
        </section>
      );
      break;
    }

    case "custom_image":
      if (!cfg.image_url) return null;
      content = (
        <section key={section.id || type} className="mb-10">
          <div className="rounded-[24px] overflow-hidden">
            <img src={cfg.image_url} alt={localizedText(locale, cfg.alt_fr, cfg.alt_en, cfg.alt || "")} className="w-full" loading="lazy"
              onError={(e) => { e.target.style.display = "none"; }} />
          </div>
        </section>
      );
      break;

    case "custom_video":
      if (!cfg.video_url) return null;
      content = (
        <section key={section.id || type} className="mb-10">
          <div className="rounded-[24px] overflow-hidden aspect-video">
            <iframe src={cfg.video_url} className="w-full h-full" title={cfg.title || "Video"} allowFullScreen loading="lazy" />
          </div>
        </section>
      );
      break;

    case "app_download":
      return null;

    default:
      return null;
  }

  if (!content) return null;

  return (
    <PreviewSectionWrapper key={section.id || type} section={section}>
      {content}
    </PreviewSectionWrapper>
  );
}

// ============ MAIN HOMEPAGE RENDERER ============

export function HomepageSections({ sections, data, locale, t, filterPublishable }) {
  const displaySections = filterPublishable
    ? sections.filter((s) => s._preview_status === "published" || (s.is_visible !== false && s.status === "published"))
    : sections;

  const sorted = [...displaySections].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  if (sorted.length === 0) {
    return (
      <>
        <HeroSliderSection sliders={[]} locale={locale} channels={data.allChannels || []} settings={data.settings} isPremium={data.isPremium} t={t} />
        <CategoryPillsSection categories={data.categories} locale={locale} t={t} onSelect={data.onSelectCategory || (() => {})} selected={data.selectedCategory} />
      </>
    );
  }

  return (
    <>
      {sorted.map((section) => renderSection(section, data, locale, t))}
      <BannerSection banners={data.banners} placement="homepage_top" locale={locale} />
      <BannerSection banners={data.banners} placement="homepage_middle" locale={locale} />
      <BannerSection banners={data.banners} placement="homepage_bottom" locale={locale} />
    </>
  );
}
